import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';

const RESOLVED_STATUSES = new Set(['resolved', 'closed']);

function sortReports(reports) {
  return [...reports].sort((a, b) => {
    const aResolved = RESOLVED_STATUSES.has(a.status);
    const bResolved = RESOLVED_STATUSES.has(b.status);
    if (aResolved !== bResolved) return aResolved ? 1 : -1;
    const aTime = aResolved ? a.updatedAt : a.createdAt;
    const bTime = bResolved ? b.updatedAt : b.createdAt;
    return new Date(bTime) - new Date(aTime);
  });
}

function formatWhen(value, locale) {
  if (!value) return '—';
  return new Date(value).toLocaleString(locale);
}

function BugReports() {
  const { t, i18n } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resolvingId, setResolvingId] = useState(null);

  const loadReports = useCallback(async () => {
    const res = await apiClient.get('/api/bug-reports');
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || t('bugReport.admin.loadFailed'));
    setReports(sortReports(data.reports || []));
  }, [t]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        await loadReports();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadReports]);

  const { openReports, resolvedReports } = useMemo(() => {
    const open = [];
    const resolved = [];
    for (const report of reports) {
      if (RESOLVED_STATUSES.has(report.status)) {
        resolved.push(report);
      } else {
        open.push(report);
      }
    }
    return { openReports: open, resolvedReports: resolved };
  }, [reports]);

  const handleResolve = async (reportId) => {
    setResolvingId(reportId);
    setError('');
    try {
      const res = await apiClient.patch(`/api/bug-reports/${encodeURIComponent(reportId)}/resolve`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('bugReport.admin.resolveFailed'));
      setReports((prev) => sortReports(
        prev.map((item) => (item._id === reportId ? data.report : item))
      ));
    } catch (e) {
      setError(e.message);
    } finally {
      setResolvingId(null);
    }
  };

  const renderReport = (report, resolved) => (
    <article
      key={report._id}
      className={`rounded-xl border p-4 ${
        resolved
          ? 'border-border/60 bg-surface-muted/40 opacity-80'
          : 'border-border bg-surface shadow-sm'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="whitespace-pre-wrap text-sm text-ink">{report.message}</p>
          <dl className="mt-3 grid gap-1 text-xs text-ink-muted sm:grid-cols-2">
            <div>
              <dt className="inline font-medium">{t('bugReport.admin.page')}: </dt>
              <dd className="inline">{report.page || t('bugReport.admin.unknownPage')}</dd>
            </div>
            <div>
              <dt className="inline font-medium">{t('bugReport.admin.reporter')}: </dt>
              <dd className="inline">{report.userId || t('bugReport.admin.anonymous')}</dd>
            </div>
            <div>
              <dt className="inline font-medium">{t('bugReport.admin.reportedAt')}: </dt>
              <dd className="inline">{formatWhen(report.createdAt, i18n.language)}</dd>
            </div>
            {resolved ? (
              <div>
                <dt className="inline font-medium">{t('bugReport.admin.resolvedAt')}: </dt>
                <dd className="inline">{formatWhen(report.updatedAt, i18n.language)}</dd>
              </div>
            ) : null}
          </dl>
        </div>
        <div className="shrink-0">
          {resolved ? (
            <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
              {t('bugReport.admin.resolvedBadge')}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => handleResolve(report._id)}
              disabled={resolvingId === report._id}
              className="rounded-full border border-link bg-accent/20 px-4 py-1.5 text-sm font-medium text-link transition hover:bg-accent/35 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resolvingId === report._id ? t('common.loading') : t('bugReport.admin.markResolved')}
            </button>
          )}
        </div>
      </div>
    </article>
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <p className="text-ink-muted">{t('bugReport.admin.loading')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-bold text-ink">{t('bugReport.admin.title')}</h1>
      <p className="mt-2 text-sm text-ink-muted">{t('bugReport.admin.subtitle')}</p>

      {error ? <p className="mt-4 text-sm text-red-700">{error}</p> : null}

      {reports.length === 0 ? (
        <p className="mt-8 text-sm text-ink-muted">{t('bugReport.admin.empty')}</p>
      ) : (
        <div className="mt-8 space-y-8">
          {openReports.length > 0 ? (
            <section>
              <h2 className="text-lg font-semibold text-ink">{t('bugReport.admin.openHeading')}</h2>
              <div className="mt-4 space-y-4">
                {openReports.map((report) => renderReport(report, false))}
              </div>
            </section>
          ) : (
            <p className="text-sm text-ink-muted">{t('bugReport.admin.noOpen')}</p>
          )}

          {resolvedReports.length > 0 ? (
            <section>
              <h2 className="text-lg font-semibold text-ink-muted">{t('bugReport.admin.resolvedHeading')}</h2>
              <div className="mt-4 space-y-4">
                {resolvedReports.map((report) => renderReport(report, true))}
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default BugReports;
